import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Select,
  Switch,
  Text,
} from "@chakra-ui/react";
import { PALETTES } from "../classroomColors";
import { STANDE } from "../standeTokens";
import { serverTime } from "./spaceTokens";

const LENGTHS = [30, 40, 45, 50, 60, 75, 90, 120];

const pad = (n) => String(n).padStart(2, "0");
const toDate = (v) => {
  const d = serverTime(v);
  return d && !Number.isNaN(d.getTime()) ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : "";
};
const toTime = (v) => {
  const d = serverTime(v);
  return d && !Number.isNaN(d.getTime()) ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : "";
};

/**
 * Making or editing a session.
 *
 * Its name, then when: leave the date empty and it stays a draft. The time
 * defaults to the start of the school day once a date is chosen. Questions
 * before class are a switch, on unless the teacher turns them off.
 *
 * A modal is portalled outside the themed page, so it takes its colours
 * from the literal palette rather than the CSS variables.
 */
const SessionForm = ({ isOpen, onClose, onSubmit, session, isDark }) => {
  const p = isDark ? PALETTES.dark : PALETTES.light;
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [length, setLength] = useState("");
  const [questions, setQuestions] = useState(true);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setName(session?.name || "");
    setDate(toDate(session?.scheduled_at));
    setTime(toTime(session?.scheduled_at) || "09:00");
    setLength(session?.duration_minutes ? String(session.duration_minutes) : "");
    setQuestions(session ? session.questions_enabled !== false : true);
    setProblem(null);
  }, [isOpen, session]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) {
      setProblem("Give the session a name.");
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      await onSubmit({
        name: name.trim(),
        scheduledAt: date ? new Date(`${date}T${time || "09:00"}`).toISOString() : "",
        durationMinutes: length ? Number(length) : null,
        questionsEnabled: questions,
      });
      onClose();
    } catch (err) {
      setProblem(err?.message || "The session could not be saved. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const field = {
    h: "44px",
    borderRadius: "12px",
    bg: p.inputBg,
    borderColor: p.border,
    color: p.text,
    fontSize: "14px",
    _placeholder: { color: p.textMuted },
    _hover: { borderColor: p.border },
    _focusVisible: { borderColor: p.text, boxShadow: "none" },
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg={p.overlay} />
      <ModalContent bg={p.page} color={p.text} borderRadius="22px" border="1px solid" borderColor={p.border} mx="16px"
        boxShadow="0 24px 64px -24px rgba(34,34,34,0.4)">
        <ModalBody as="form" onSubmit={submit} p="24px" display="flex" flexDirection="column" gap="12px">
          <Box mb="2px">
            <Text fontSize="19px" fontWeight="600">{session ? "Edit session" : "New session"}</Text>
            <Text fontSize="13px" color={p.textSub}>Leave the date empty to keep it as a draft.</Text>
          </Box>
          <Input {...field} value={name} onChange={(e) => setName(e.target.value)} placeholder="Electricity: circuits deep-dive" autoFocus aria-label="Session name" />
          <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1.3fr 1fr 1fr" }} gap="10px">
            <Input {...field} type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Date" />
            <Input {...field} type="time" value={time} onChange={(e) => setTime(e.target.value)} isDisabled={!date} aria-label="Start time" />
            <Select {...field} value={length} onChange={(e) => setLength(e.target.value)} aria-label="Length">
              <option value="">Length</option>
              {LENGTHS.map((m) => (
                <option key={m} value={m}>{m < 60 || m % 60 ? `${m} min` : `${m / 60} h`}</option>
              ))}
            </Select>
          </Box>
          <Flex as="label" gap="12px" align="flex-start" p="12px 14px" borderRadius="14px" bg={p.surface} border="1px solid" borderColor={p.border} cursor="pointer">
            <Switch
              isChecked={questions}
              onChange={(e) => setQuestions(e.target.checked)}
              mt="2px"
              sx={{
                "span.chakra-switch__track": { bg: p.border },
                "span.chakra-switch__track[data-checked]": { bg: p.solidBg },
                "span.chakra-switch__thumb": { bg: p.page },
              }}
            />
            <Box>
              <Text fontSize="13.5px" fontWeight="600">Questions before class</Text>
              <Text fontSize="12.5px" color={p.textSub}>
                Students can send what they want covered. You'll see them grouped before the session.
              </Text>
            </Box>
          </Flex>
          {problem && <Text fontSize="12.5px" color={p.accentSoftText} role="alert">{problem}</Text>}
          <Flex justify="flex-end" gap="8px" mt="4px">
            <Button variant="ghost" h="42px" borderRadius="12px" fontSize="13.5px" color={p.textSub} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" h="42px" px="18px" borderRadius="12px" fontSize="13.5px" fontWeight="600" isLoading={saving}
              bg={STANDE.primaryBlue} color={STANDE.black} _hover={{ bg: STANDE.primaryBlue, filter: "brightness(0.94)" }}>
              {session ? "Save session" : "Create session"}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SessionForm;
